import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Check, Sparkles, Send, MessageSquare, GitBranch, AlertCircle, Download, Folder } from "lucide-react";
import { api, type SpotlightFieldState, type SocialCheckIssue } from "../../lib/api.js";

/** Campaign status as the operator reads it. Shared with the campaign strip. */
export const CAMPAIGN_STATUS_HE: Record<string, string> = {
  draft: "טיוטה",
  sent: "נשלח למטפל/ת, ממתין לתשובה",
  feedback: "התקבלה תשובה / התבקש שינוי",
  revised: "עודכן",
  approved: "המטפל/ת אישרו ✓",
  scheduled: "מתוזמן",
  published: "פורסם",
  declined: "המטפל/ת סירבו",
  blocked: "חסום",
};

/**
 * The per-field editor.
 *
 * The state this UI exists to make visible: for every word on the card, WHO put
 * it there. A model-written quote and a therapist-supplied quote look identical
 * once rendered, and they carry very different authority — so origin is a badge
 * on every row, and the fields that can misrepresent a person (quote, photo,
 * captions) cannot be published until a human signs them off.
 */

const FIELD_LABELS: Record<string, string> = {
  heading: "כותרת השקופית",
  tagline: "כותרת המטפל/ת",
  quote: "ציטוט",
  photo: "תמונה",
  methods: "שיטות",
  cities: "מיקומים",
  caption_ig: "כיתוב אינסטגרם",
  caption_fb: "כיתוב פייסבוק",
  hashtags: "האשטגים",
  alt_text: "אלט־טקסט",
};

const ORIGIN: Record<string, { label: string; cls: string; title: string }> = {
  generated: { label: "מהמערכת", cls: "bg-canvas text-muted", title: "נוצר על ידי המערכת או נשלף מהפרופיל. דורש אישור." },
  therapist: { label: "מהמטפל/ת", cls: "bg-brand-50 text-brand-800", title: "המטפל/ת נתנו את הערך הזה. עדיין דורש אישור לפני פרסום." },
  operator: { label: "נערך ידנית", cls: "bg-emerald-50 text-emerald-800", title: "נערך ואושר בממשק." },
};

const SOURCE_HE: Record<string, string> = { system: "מהמערכת", feedback: "מפידבק", operator: "עריכה ידנית" };

/**
 * Delivery, shown separately from campaign status.
 *
 * "Sent" only means Meta accepted it. The spotlight template is MARKETING, so a
 * therapist who opted out of marketing never receives it — and without this pill
 * that looks identical to someone who received it and chose not to answer.
 */
const DELIVERY_HE: Record<string, { label: string; cls: string; title: string }> = {
  sent: { label: "נשלח, טרם נמסר", cls: "bg-amber-50 text-amber-900", title: "מטא קיבלה את ההודעה אבל עדיין לא אישרה מסירה למכשיר. שקט במצב הזה אינו הסכמה." },
  delivered: { label: "נמסר", cls: "bg-emerald-50 text-emerald-800", title: "ההודעה הגיעה למכשיר." },
  read: { label: "נקרא", cls: "bg-emerald-50 text-emerald-800", title: "ההודעה נקראה." },
  failed: { label: "לא נמסר", cls: "bg-rose-50 text-rose-900", title: "המסירה נכשלה. ייתכן שהמטפל/ת ביטלו הסכמה לדיוור שיווקי." },
};

/** Slide names, for the "not exported" list. Distinct from FIELD_LABELS: those
 *  are editable fields, these are slides, and only some names overlap. */
const SLIDE_LABELS: Record<string, string> = {
  cover: "שער", methods: "טיפולים", where: "איפה ואיך",
  quote: "ציטוט", reviews: "ביקורות", cta: "צרו קשר",
};

export function SpotlightEditor({ slug, name, onCampaign, format, theme, order, scale }: {
  slug: string; name: string; onCampaign: (id: string | null) => void;
  format: string; theme: string; order: string[]; scale: Record<string, number>;
}) {
  const qc = useQueryClient();
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [imageFormat, setImageFormat] = useState("png");
  const [density, setDensity] = useState<1 | 2>(1);

  const create = useMutation({
    mutationFn: () => api.createSpotlightCampaign(slug),
    onSuccess: (r) => {
      setCampaignId(r.detail.campaign.id);
      onCampaign(r.detail.campaign.id);
      void qc.invalidateQueries({ queryKey: ["spotlight", "campaigns"] });
    },
  });

  /**
   * Re-open this therapist's open campaign on arrival.
   *
   * The campaign id lived only in this component's state, so a page reload — or
   * picking another therapist and coming back — showed "create a campaign" for a
   * person who already had one, mid-conversation. Pressing it returned the same
   * campaign, which worked, and looked like starting over.
   */
  const campaignsQuery = useQuery({ queryKey: ["spotlight", "campaigns"], queryFn: () => api.listSpotlightCampaigns() });
  const openForSlug = (campaignsQuery.data?.items ?? []).find(
    (c) => c.therapistSlug === slug && c.status !== "published" && c.status !== "declined",
  );
  useEffect(() => {
    if (!campaignId && openForSlug) {
      setCampaignId(openForSlug.id);
      onCampaign(openForSlug.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, openForSlug?.id]);

  const detailQuery = useQuery({
    queryKey: ["spotlight", "campaign", campaignId],
    queryFn: () => api.getSpotlightCampaign(campaignId!),
    enabled: !!campaignId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["spotlight", "campaign", campaignId] });
    // The iframes read the campaign, so they have to re-fetch too.
    onCampaign(campaignId);
  };

  const genCopy = useMutation({ mutationFn: () => api.generateSpotlightCopy(campaignId!), onSuccess: invalidate });
  const sendWa = useMutation({ mutationFn: () => api.sendSpotlightWhatsapp(campaignId!, false), onSuccess: invalidate });
  const feedback = useMutation({
    mutationFn: () => api.sendSpotlightFeedback(campaignId!, feedbackText),
    onSuccess: () => { setFeedbackText(""); invalidate(); },
  });

  /**
   * Save the arrangement, then render.
   *
   * One action, in that order, on purpose: the operator arranged the carousel on
   * screen and expects THAT. Exporting without persisting first would render
   * whatever was last saved, and the mismatch would only show up in the files.
   */
  const doExport = useMutation({
    mutationFn: async () => {
      await api.saveSpotlightRenderSettings(campaignId!, { slideOrder: order, theme, format, sizeScale: scale });
      return api.exportSpotlight(campaignId!, { format, theme, imageFormat, density, slides: order });
    },
  });

  const d = detailQuery.data;

  /**
   * THE MANUAL REQUEST. Until the roster carries phone numbers and Meta approved
   * a template, the operator asks the therapist from their own WhatsApp: the
   * cover image, a prepared message, then "sent" marked here. Offered only
   * when nothing blocks asking — the server refuses the "sent" mark otherwise.
   */
  const canAskManually = !!d && d.campaign.status === "draft" && d.askBlockers.length === 0;
  const manualQuery = useQuery({
    queryKey: ["spotlight", "manual", campaignId, d?.current?.id, d?.campaign.updatedAt],
    queryFn: () => api.spotlightManualRequest(campaignId!),
    enabled: canAskManually,
  });
  const [copied, setCopied] = useState(false);
  const [sentArmed, setSentArmed] = useState(false);
  const markSentMut = useMutation({
    mutationFn: () => api.markSpotlightSent(campaignId!),
    onSuccess: () => {
      setSentArmed(false);
      invalidate();
      void qc.invalidateQueries({ queryKey: ["spotlight", "campaigns"] });
    },
    onError: () => setSentArmed(false),
  });
  // Silence is not consent. After three days the operator is told, not left to notice.
  const waitingTooLong = !!d && d.campaign.status === "sent" && !!d.campaign.sentAt
    && Date.now() - Date.parse(d.campaign.sentAt) > 3 * 864e5;

  if (!campaignId) {
    return (
      <div className="rounded-lg border border-line bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-muted">
            צור קמפיין כדי לערוך את השדות, לייצר ציטוט וכיתובים, ולנהל את הפידבק מ<span dir="rtl">{name}</span>.
          </p>
          <button onClick={() => create.mutate()} disabled={create.isPending} className="btn-primary shrink-0 text-2xs">
            {create.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <GitBranch className="h-3 w-3" />}
            צור קמפיין
          </button>
        </div>
        {create.isError && (
          <div className="mt-2 flex items-start gap-1.5 rounded border border-rose-300 bg-rose-50 px-2 py-1 text-2xs text-rose-900">
            <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" />{(create.error as Error).message}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-lg border border-line bg-white p-4">
      {detailQuery.isLoading && <div className="flex items-center gap-2 text-xs text-muted"><Loader2 className="h-3 w-3 animate-spin" /> טוען…</div>}

      {d && (
        <>
          <div className="flex flex-wrap items-center gap-2 text-2xs">
            <span className="font-mono text-muted">{d.campaign.id}</span>
            <span className="rounded-full bg-canvas px-2 py-0.5 text-muted">סטטוס: {d.campaign.status}</span>
            <span className="rounded-full bg-canvas px-2 py-0.5 text-muted">v{d.campaign.revision}</span>
            {d.delivery?.status && (
              <span
                className={`rounded-full px-2 py-0.5 ${DELIVERY_HE[d.delivery.status]?.cls ?? "bg-canvas text-muted"}`}
                title={d.delivery.error ?? DELIVERY_HE[d.delivery.status]?.title}
              >
                {DELIVERY_HE[d.delivery.status]?.label ?? d.delivery.status}
              </span>
            )}
            {d.publishable
              ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-800">מוכן לפרסום</span>
              : !d.coverApproved
                ? <span className="rounded-full bg-rose-50 px-2 py-0.5 text-rose-900"
                    title="שקופית השער היא ההחלטה אם לפרסם בכלל. עד שהיא מאושרת אי אפשר לאשר שום שקופית אחרת, ושום דבר לא מיוצא.">
                    השער לא אושר: {d.coverPending.map((f) => FIELD_LABELS[f] ?? f).join(" · ")}
                  </span>
                : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-amber-900" title="שדות שחייבים אישור לפני פרסום">
                    ממתין לאישור: {d.awaitingApproval.map((f) => FIELD_LABELS[f] ?? f).join(" · ")}
                  </span>}
            <button onClick={() => genCopy.mutate()} disabled={genCopy.isPending} className="btn-secondary ml-auto text-2xs"
              title="קריאת מודל — עולה כסף. מייצר ציטוט, כיתובים והאשטגים. התוצאה נשמרת כלא-מאושרת.">
              {genCopy.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              ייצר ציטוט וכיתובים
            </button>
            {d.campaign.status === "draft" ? (
              <button onClick={() => sendWa.mutate()} disabled={sendWa.isPending || d.askBlockers.length > 0}
                className="btn-primary text-2xs disabled:cursor-not-allowed disabled:opacity-40"
                title={d.askBlockers.length > 0
                  ? `לפני בקשת אישור: ${d.askBlockers.join(" · ")}`
                  : "שולח את הקמפיין למטפל/ת בוואטסאפ עם כפתורי אישור / בקשת שינוי."}>
                {sendWa.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageSquare className="h-3 w-3" />}
                שלח למטפל/ת בוואטסאפ
              </button>
            ) : (
              <span className="rounded-full bg-canvas px-2 py-0.5 text-muted" title="מצב הקמפיין מול המטפל/ת">
                {CAMPAIGN_STATUS_HE[d.campaign.status] ?? d.campaign.status}
              </span>
            )}
          </div>
          {/* A person who asked not to be published. Said first and plainly:
              every action below is refused on the server anyway. */}
          {d.optedOut && <Err msg="המטפל/ת ביקשו לא להתפרסם. אין בקשת אישור, ייצוא או פרסום." />}
          {/* What stands between this draft and asking the therapist. Listed,
              because a greyed-out send with no reason looks broken. */}
          {!d.optedOut && d.campaign.status === "draft" && d.askBlockers.length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-2xs text-amber-900">
              <p className="font-medium">לפני שמבקשים אישור מהמטפל/ת:</p>
              <ul className="mt-0.5 list-disc ps-4">
                {d.askBlockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}
          {canAskManually && (
            <div className="space-y-1.5 rounded-lg border border-brand-200 bg-brand-50/40 p-3 text-2xs">
              <p className="font-medium text-ink">בקשת אישור מהמטפל/ת, מהוואטסאפ שלך</p>
              <p className="text-muted">
                1. מורידים את תמונת השער. 2. מעתיקים את ההודעה, או פותחים אותה בוואטסאפ ובוחרים את השיחה.
                3. מצרפים את התמונה ושולחים. 4. מסמנים כאן שנשלח. בלי אישור של המטפל/ת לא מפרסמים.
              </p>
              {manualQuery.data?.message && (
                <textarea
                  readOnly dir="rtl" rows={8} value={manualQuery.data.message}
                  className="w-full rounded border border-line bg-white p-2 text-xs leading-relaxed"
                />
              )}
              <div className="flex flex-wrap items-center gap-2">
                <a href={api.spotlightPreviewPngUrl(campaignId, "cover")} download className="btn-secondary text-2xs"
                  title="השער כמו שיתפרסם, בפורמט ובעיצוב השמורים לקמפיין">
                  <Download className="h-3 w-3" /> הורדת תמונת השער
                </a>
                <button
                  onClick={async () => {
                    if (!manualQuery.data?.message) return;
                    await navigator.clipboard.writeText(manualQuery.data.message);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  disabled={!manualQuery.data?.message}
                  className="btn-secondary text-2xs"
                >
                  {copied ? <Check className="h-3 w-3" /> : null}
                  {copied ? "הועתק" : "העתקת ההודעה"}
                </button>
                {manualQuery.data?.waLink && (
                  <a href={manualQuery.data.waLink} target="_blank" rel="noreferrer" className="btn-secondary text-2xs"
                    title="פותח וואטסאפ עם ההודעה. בוחרים את השיחה עם המטפל/ת ומצרפים את התמונה.">
                    <MessageSquare className="h-3 w-3" /> פתיחה בוואטסאפ
                  </a>
                )}
                <button
                  onClick={() => (sentArmed ? markSentMut.mutate() : setSentArmed(true))}
                  disabled={markSentMut.isPending}
                  className={`btn-primary ml-auto text-2xs ${sentArmed ? "ring-2 ring-brand-300" : ""}`}
                  title="מסמן שהבקשה נשלחה. מכאן, עריכה יוצרת גרסה חדשה, כדי שמה שהמטפל/ת ראו יישמר."
                >
                  {markSentMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                  {sentArmed ? "לאשר: ההודעה נשלחה" : "סימנתי שנשלח"}
                </button>
              </div>
              {markSentMut.isError && <Err msg={(markSentMut.error as Error).message} />}
            </div>
          )}
          {d.campaign.status === "sent" && (
            <div className={`rounded border px-2 py-1.5 text-2xs ${waitingTooLong ? "border-amber-300 bg-amber-50 text-amber-900" : "border-sky-200 bg-sky-50 text-sky-900"}`}>
              הבקשה נשלחה{d.campaign.sentAt ? ` ב־${new Date(d.campaign.sentAt).toLocaleString("he-IL")}` : ""}. כשהתשובה מגיעה, מדביקים אותה למטה.
              {waitingTooLong && (
                <span className="mt-0.5 block font-medium">
                  עברו יותר מ־3 ימים בלי תשובה. שתיקה היא לא הסכמה: אפשר לשלוח תזכורת, ובלי תשובה לא מפרסמים.
                </span>
              )}
            </div>
          )}
          {genCopy.isError && <Err msg={(genCopy.error as Error).message} />}
          {sendWa.isError && <Err msg={(sendWa.error as Error).message} />}
          {sendWa.data && !sendWa.data.ok && <Err msg={sendWa.data.result?.error ?? sendWa.data.error ?? "שליחה נכשלה"} />}

          {/* revision trail */}
          {d.history.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5 rounded bg-canvas px-2 py-1.5 text-2xs text-muted">
              <span>גרסאות:</span>
              {d.history.map((v) => (
                <span key={v.id} className={`rounded px-1.5 py-0.5 ${v.id === d.current?.id ? "bg-brand-100 font-medium text-brand-800" : "bg-white"}`}>
                  v{v.revision} · {SOURCE_HE[v.source] ?? v.source}
                </span>
              ))}
            </div>
          )}

          <div className="divide-y divide-line">
            {d.editable.map((f) => {
              const state = d.current?.fields.find((x) => x.field === f);
              const isCover = d.coverFields.includes(f);
              return (
                <FieldRow
                  key={f} campaignId={campaignId} field={f} state={state} onChanged={invalidate}
                  issues={d.captionIssues?.find((c) => c.field === f)?.issues ?? []}
                  isCover={isCover}
                  approveBlocked={!isCover && !d.coverApproved}
                  blockReason={`יש לאשר קודם את שקופית השער (${d.coverPending.map((x) => FIELD_LABELS[x] ?? x).join(" · ")})`}
                />
              );
            })}
          </div>

          {/* export */}
          <div className="space-y-1.5 rounded-lg border border-line bg-canvas p-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1.5 text-2xs font-medium text-ink"><Download className="h-3 w-3" /> ייצוא</span>
              <select value={imageFormat} onChange={(e) => setImageFormat(e.target.value)} className="rounded border border-line bg-white px-1.5 py-0.5 text-2xs">
                <option value="png">PNG</option>
                <option value="jpeg">JPEG</option>
                <option value="webp">WebP</option>
              </select>
              <select value={density} onChange={(e) => setDensity(Number(e.target.value) as 1 | 2)} className="rounded border border-line bg-white px-1.5 py-0.5 text-2xs">
                <option value={1}>1x · 1080px</option>
                <option value={2}>2x · 2160px</option>
              </select>
              <span className="text-2xs text-muted">{order.length} שקופיות · {format}</span>
              <button
                onClick={() => doExport.mutate()}
                disabled={doExport.isPending || !d.coverApproved}
                title={d.coverApproved ? "שומר את הסדר והגדלים ואז מרנדר" : "יש לאשר את שקופית השער לפני ייצוא"}
                className="btn-primary ml-auto text-2xs disabled:cursor-not-allowed disabled:opacity-40"
              >
                {doExport.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                {doExport.isPending ? "מרנדר…" : "ייצא קרוסלה"}
              </button>
            </div>
            {!d.coverApproved && (
              <p className="text-2xs text-muted">אשר את שקופית השער כדי לפתוח ייצוא.</p>
            )}
            {doExport.isError && <Err msg={(doExport.error as Error).message} />}
            {doExport.data && (
              <div className="space-y-1 rounded border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-2xs text-emerald-900">
                <div className="flex items-center gap-1.5 font-medium">
                  <Check className="h-3 w-3" /> יוצאו {doExport.data.files.length} קבצים
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                  {doExport.data.files.map((f) => (
                    <span key={f.slide} className="font-mono">{f.slide} · {Math.round(f.bytes / 1024)}KB</span>
                  ))}
                </div>
                {doExport.data.dropped.length > 0 && (
                  <div className="text-amber-900">
                    לא יוצאו: {doExport.data.dropped.map((x) => `${SLIDE_LABELS[x.slide] ?? x.slide} (${x.reason})`).join(" · ")}
                  </div>
                )}
                <div className="flex items-start gap-1 font-mono text-[10px] text-emerald-800" dir="ltr">
                  <Folder className="mt-0.5 h-2.5 w-2.5 shrink-0" />
                  <span className="break-all">{doExport.data.dir}</span>
                </div>
              </div>
            )}
          </div>

          {/* feedback intake */}
          <div className="space-y-1.5 rounded-lg border border-line bg-canvas p-3">
            <div className="flex items-center gap-1.5 text-2xs font-medium text-ink">
              <MessageSquare className="h-3 w-3" /> תשובה מהמטפל/ת
            </div>
            <p className="text-2xs text-muted">
              הדבק את מה שהם כתבו. המערכת תזהה מה הם מבקשים, תיישם ערכים שהם נתנו, ותיצור גרסה חדשה.
              בקשה להחליף תמונה מדומיין חיצוני לא תיושם אוטומטית.
            </p>
            {d.campaign.status === "draft" && (
              <p className="text-2xs text-amber-800">
                הקמפיין עוד לא סומן כנשלח. תשובה כמו "מאושר" תירשם כמשוב ולא כאישור — סמנו קודם שהבקשה נשלחה.
              </p>
            )}
            <textarea
              value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)} rows={3}
              placeholder="למשל: תחליפו את הציטוט ל…"
              className="w-full rounded border border-line bg-white p-2 text-xs"
            />
            <div className="flex items-center gap-2">
              <button onClick={() => feedback.mutate()} disabled={feedback.isPending || feedbackText.trim().length < 2}
                className="btn-primary text-2xs">
                {feedback.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                נתח ויישם
              </button>
              {feedback.data && (
                <span className="text-2xs text-muted">
                  זוהה: <b>{feedback.data.analysis.intent}</b> · {feedback.data.analysis.summaryHe}
                </span>
              )}
            </div>
            {feedback.isError && <Err msg={(feedback.error as Error).message} />}
            {feedback.data?.analysis.needsProfileEdit && (
              <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-2xs text-amber-900">
                דורש עדכון בפרופיל שלהם באתר (איננו יכולים לערוך): {feedback.data.analysis.profileEditHe}
              </div>
            )}
            {(feedback.data?.deferred?.length ?? 0) > 0 && (
              <div className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-2xs text-amber-900">
                לא יושם אוטומטית: {feedback.data!.deferred.map((x) => `${FIELD_LABELS[x.field] ?? x.field} (${x.reason})`).join(" · ")}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FieldRow({ campaignId, field, state, onChanged, issues, isCover, approveBlocked: coverBlocked, blockReason: coverReason }: {
  campaignId: string; field: string; state: SpotlightFieldState | undefined; onChanged: () => void;
  /** Social checks on this field's saved value — captions only. */
  issues: SocialCheckIssue[];
  isCover: boolean; approveBlocked: boolean; blockReason: string;
}) {
  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity !== "error");
  // A caption the checks refuse cannot be approved; the server refuses it too.
  const approveBlocked = coverBlocked || errors.length > 0;
  const blockReason = errors.length > 0 ? "הכיתוב נכשל בבדיקות — צריך לתקן לפני אישור" : coverReason;
  const [draft, setDraft] = useState(state?.value ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [last, setLast] = useState(state?.value ?? "");
  if ((state?.value ?? "") !== last && draft === last) { setLast(state?.value ?? ""); setDraft(state?.value ?? ""); }

  const dirty = draft !== (state?.value ?? "");
  const origin = ORIGIN[state?.origin ?? "generated"]!;
  const long = field.startsWith("caption") || field === "quote";

  const save = async () => {
    setBusy(true); setErr(null);
    try { await api.editSpotlightField(campaignId, field, draft.trim() || null); onChanged(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };
  const toggle = async () => {
    setBusy(true); setErr(null);
    try { await api.approveSpotlightField(campaignId, field, !state?.approved); onChanged(); }
    catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="py-2">
      <div className="mb-1 flex items-center gap-2">
        <span className="text-2xs font-medium text-ink">{FIELD_LABELS[field] ?? field}</span>
        {isCover && (
          <span className="rounded bg-brand-50 px-1 text-[10px] text-brand-800" title="שדה של שקופית השער — הוא שמחליט אם מפרסמים בכלל.">
            שער
          </span>
        )}
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${origin.cls}`} title={origin.title}>{origin.label}</span>
        {state?.approved
          ? <span className="flex items-center gap-0.5 text-[10px] text-emerald-700"><Check className="h-2.5 w-2.5" />מאושר</span>
          : state?.value ? <span className="text-[10px] text-amber-700">לא מאושר</span> : null}
        {state?.sourceQuote && (
          <span className="truncate text-[10px] text-muted" dir="rtl" title={state.sourceQuote}>← "{state.sourceQuote}"</span>
        )}
        <div className="ml-auto flex items-center gap-1">
          {state?.value && (
            <button
              onClick={toggle}
              disabled={busy || (approveBlocked && !state.approved)}
              title={approveBlocked && !state.approved ? blockReason : undefined}
              className="rounded border border-line px-1.5 py-0.5 text-[10px] hover:bg-canvas disabled:cursor-not-allowed disabled:opacity-40"
            >
              {state.approved ? "בטל אישור" : "אשר"}
            </button>
          )}
          {dirty && (
            <button onClick={save} disabled={busy} className="btn-primary px-2 py-0.5 text-[10px]">
              {busy ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : null} שמור
            </button>
          )}
        </div>
      </div>
      {long
        ? <textarea dir="rtl" value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} className="w-full rounded border border-line p-1.5 text-xs" />
        : <input dir="rtl" value={draft} onChange={(e) => setDraft(e.target.value)} className="w-full rounded border border-line px-1.5 py-1 text-xs" />}
      {(errors.length > 0 || warnings.length > 0) && (
        <ul className="mt-1 space-y-0.5 text-[10px]">
          {errors.map((i, k) => <li key={`e${k}`} className="text-rose-800">✕ [{i.rule}] {i.message}</li>)}
          {warnings.map((i, k) => <li key={`w${k}`} className="text-amber-800">! [{i.rule}] {i.message}</li>)}
        </ul>
      )}
      {err && <Err msg={err} />}
    </div>
  );
}

function Err({ msg }: { msg: string }) {
  return (
    <div className="mt-1 flex items-start gap-1.5 rounded border border-rose-300 bg-rose-50 px-2 py-1 text-2xs text-rose-900">
      <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" /><span>{msg}</span>
    </div>
  );
}
